/**
 * 媒体文件下载服务
 * 
 * 负责从服务器下载项目中引用的媒体文件（图片、文件等）到本地 IndexedDB
 */

import type { Project, Item } from '../types/data'
import { getMediaFromUserDB, saveMediaToUserDB } from './dbManager'
import { getStorageConfig, getOssStsToken } from './api'

// 获取 API 基础 URL
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL
  }
  return '/api'
}

/**
 * 从 fileId 中提取原始 UUID（去掉扩展名）
 */
function extractOriginalFileId(fileId: string): string {
  if (fileId && fileId.includes('.')) {
    return fileId.substring(0, fileId.lastIndexOf('.'))
  }
  return fileId
}

/**
 * 检查本地 media 表是否已有该文件
 */
async function isMediaExistsLocally(userUuid: string, fileId: string): Promise<boolean> {
  try {
    const originalFileId = extractOriginalFileId(fileId)
    const media = await getMediaFromUserDB(userUuid, originalFileId)
    return media !== null
  } catch {
    return false
  }
}

/**
 * 从服务器下载文件并保存到本地 IndexedDB
 * 
 * @param sourceUserUuid 源用户 UUID（用于构建下载路径）
 * @param fileId 文件 ID
 * @param projectId 项目 ID
 * @param storageConfig 存储配置
 * @param targetUserUuid 可选，保存到哪个用户的 IndexedDB（默认与 sourceUserUuid 相同）
 */
async function downloadAndSaveMedia(
  sourceUserUuid: string,
  fileId: string,
  projectId: string,
  storageConfig: { useOSS: boolean; provider?: string; bucket?: string | null; region?: string | null; endpoint?: string | null } | null,
  targetUserUuid?: string
): Promise<boolean> {
  const originalFileId = extractOriginalFileId(fileId)
  const saveToUserUuid = targetUserUuid || sourceUserUuid
  
  try {
    let blob: Blob
    let fileName = fileId
    let fileType = 'application/octet-stream'
    
    if (storageConfig?.useOSS) {
      // 从对象存储下载（使用源用户的路径）
      blob = await downloadFromOSS(sourceUserUuid, projectId, fileId, storageConfig)
    } else {
      // 从服务器 API 下载
      // 后端 API 路由格式: /api/files/:userUuid/:projectId/:fileId
      const apiBaseUrl = getApiBaseUrl()
      const downloadUrl = `${apiBaseUrl}/files/${sourceUserUuid}/${projectId}/${fileId}`
      
      console.log(`[mediaDownloader] 正在下载: ${downloadUrl}`)
      
      const response = await fetch(downloadUrl)
      if (!response.ok) {
        // 如果文件不存在（404），可能是文件还没上传到服务器，跳过
        if (response.status === 404) {
          console.warn(`[mediaDownloader] 文件不存在于服务器: ${fileId}`)
          return false
        }
        throw new Error(`下载失败: ${response.status} ${response.statusText}`)
      }
      
      blob = await response.blob()
      
      // 尝试从响应头获取文件信息
      const contentDisposition = response.headers.get('content-disposition')
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (match) {
          fileName = match[1].replace(/['"]/g, '')
        }
      }
      fileType = response.headers.get('content-type') || fileType
    }
    
    // 提取扩展名
    let ext = ''
    if (fileName.includes('.')) {
      ext = fileName.split('.').pop() || ''
    } else if (fileType.includes('/')) {
      ext = fileType.split('/').pop() || ''
    }
    
    // 保存到目标用户的 IndexedDB
    const mediaData = {
      id: originalFileId,
      blob: blob,
      fileName: fileName,
      fileType: fileType,
      fileSize: blob.size,
      ext: ext,
      createdAt: new Date().toISOString()
    }
    
    await saveMediaToUserDB(saveToUserUuid, mediaData)
    console.log(`[mediaDownloader] 已下载并保存: ${fileId} (${blob.size} bytes) -> 用户 ${saveToUserUuid}`)
    return true
  } catch (error) {
    console.error(`[mediaDownloader] 下载文件失败 (${fileId}):`, error)
    return false
  }
}

/**
 * 从对象存储（阿里云 OSS / 腾讯云 COS）下载文件
 * 会尝试多种文件名格式（带扩展名和不带扩展名）
 */
async function downloadFromOSS(
  userUuid: string,
  projectId: string,
  fileId: string,
  storageConfig: { useOSS: boolean; provider?: string; bucket?: string | null; region?: string | null; endpoint?: string | null }
): Promise<Blob> {
  // 获取 STS 临时凭证
  const stsToken = await getOssStsToken()
  if (!stsToken) {
    throw new Error('获取 OSS STS 凭证失败')
  }
  
  // 构建要尝试的文件名列表
  // 如果 fileId 已经带扩展名，直接使用；否则尝试常见的图片扩展名
  const fileIdsToTry: string[] = [fileId]
  if (!fileId.includes('.')) {
    // 尝试常见的图片扩展名
    fileIdsToTry.push(`${fileId}.png`, `${fileId}.jpg`, `${fileId}.jpeg`, `${fileId}.gif`, `${fileId}.webp`)
  }
  
  let lastError: Error | null = null
  
  for (const tryFileId of fileIdsToTry) {
    const objectKey = `${userUuid}/${projectId}/${tryFileId}`
    
    try {
      if (storageConfig.provider === 'tencent') {
        // 腾讯云 COS
        const { bucket, region } = stsToken
        const url = `https://${bucket}.cos.${region}.myqcloud.com/${objectKey}`
        
        const response = await fetch(url, {
          headers: {
            'Authorization': generateCosAuthorization(stsToken, 'GET', objectKey)
          }
        })
        
        if (response.ok) {
          console.log(`[mediaDownloader] OSS 下载成功: ${objectKey}`)
          return await response.blob()
        }
        
        if (response.status !== 404) {
          lastError = new Error(`COS 下载失败: ${response.status}`)
        }
      } else {
        // 阿里云 OSS（默认）
        const { bucket, region, endpoint } = stsToken
        const ossEndpoint = endpoint || `https://${bucket}.${region}.aliyuncs.com`
        const url = `${ossEndpoint}/${objectKey}`
        
        const response = await fetch(url, {
          headers: {
            'Authorization': generateOssAuthorization(stsToken, 'GET', objectKey)
          }
        })
        
        if (response.ok) {
          console.log(`[mediaDownloader] OSS 下载成功: ${objectKey}`)
          return await response.blob()
        }
        
        if (response.status !== 404) {
          lastError = new Error(`OSS 下载失败: ${response.status}`)
        }
      }
    } catch (error) {
      lastError = error as Error
    }
  }
  
  // 所有尝试都失败了
  throw lastError || new Error(`OSS 下载失败: 文件不存在 (尝试了 ${fileIdsToTry.join(', ')})`)
}

/**
 * 生成阿里云 OSS 签名（简化版，实际可能需要更完整的签名）
 */
function generateOssAuthorization(stsToken: any, _method: string, _objectKey: string): string {
  // 简化实现：直接使用 STS Token
  // 实际生产环境可能需要更完整的签名算法
  return `Bearer ${stsToken.credentials.securityToken}`
}

/**
 * 生成腾讯云 COS 签名（简化版）
 */
function generateCosAuthorization(stsToken: any, _method: string, _objectKey: string): string {
  // 简化实现：直接使用 STS Token
  return `Bearer ${stsToken.credentials.securityToken}`
}

/**
 * 收集项目中所有需要下载的媒体文件 ID
 */
function collectMediaFileIds(projects: Project[]): Map<string, { fileId: string; projectId: string }> {
  const mediaFiles = new Map<string, { fileId: string; projectId: string }>()
  
  for (const project of projects) {
    const projectId = project.uuid || project.id
    if (!projectId) continue
    
    // 收集项目封面
    if (project.coverThumbnailFileId) {
      const originalId = extractOriginalFileId(project.coverThumbnailFileId)
      if (!mediaFiles.has(originalId)) {
        mediaFiles.set(originalId, { fileId: project.coverThumbnailFileId, projectId })
      }
    }
    
    // 收集 items 中的媒体文件
    const allItems: Item[] = [
      ...(project.items || []),
      ...(project.groups?.flatMap(g => g.items) || [])
    ]
    
    for (const item of allItems) {
      // 图片和文件类型的 item
      if ((item.type === 'image' || item.type === 'file') && item.fileId) {
        const originalId = extractOriginalFileId(item.fileId)
        if (!mediaFiles.has(originalId)) {
          mediaFiles.set(originalId, { fileId: item.fileId, projectId })
        }
      }
      
      // 缩略图
      if (item.thumbnailFileId) {
        const originalId = extractOriginalFileId(item.thumbnailFileId)
        if (!mediaFiles.has(originalId)) {
          mediaFiles.set(originalId, { fileId: item.thumbnailFileId, projectId })
        }
      }
    }
  }
  
  return mediaFiles
}

/**
 * 下载所有项目中引用的媒体文件
 * 
 * @param userUuid 用于下载文件的用户 UUID（文件存储路径中的用户）
 * @param projects 项目列表
 * @param saveToUserUuid 可选，保存到哪个用户的 IndexedDB（默认与 userUuid 相同）
 *                       用于 Fork 场景：从原用户路径下载，保存到当前用户的 IndexedDB
 * @returns 下载结果统计
 */
export async function downloadMediaFilesFromServer(
  userUuid: string,
  projects: Project[],
  saveToUserUuid?: string
): Promise<{ success: number; failed: number; skipped: number }> {
  let success = 0
  let failed = 0
  let skipped = 0
  
  // 如果没有指定保存目标用户，默认保存到下载用户
  const targetUserUuid = saveToUserUuid || userUuid
  
  // 1. 收集所有需要下载的媒体文件
  const mediaFiles = collectMediaFileIds(projects)
  console.log(`[mediaDownloader] 共发现 ${mediaFiles.size} 个媒体文件需要检查`)
  console.log(`[mediaDownloader] 下载源用户: ${userUuid}, 保存目标用户: ${targetUserUuid}`)
  
  if (mediaFiles.size === 0) {
    return { success: 0, failed: 0, skipped: 0 }
  }
  
  // 2. 获取存储配置
  const storageConfig = await getStorageConfig()
  console.log(`[mediaDownloader] 存储配置: ${storageConfig?.useOSS ? '使用对象存储' : '使用服务器存储'}`)
  
  // 3. 遍历并下载缺失的文件
  for (const [originalId, { fileId, projectId }] of mediaFiles) {
    try {
      // 检查目标用户的本地是否已有
      const exists = await isMediaExistsLocally(targetUserUuid, originalId)
      if (exists) {
        skipped++
        continue
      }
      
      // 从源用户路径下载，保存到目标用户的 IndexedDB
      const downloaded = await downloadAndSaveMedia(userUuid, fileId, projectId, storageConfig, targetUserUuid)
      if (downloaded) {
        success++
      } else {
        failed++
      }
    } catch (error) {
      console.error(`[mediaDownloader] 处理文件失败 (${fileId}):`, error)
      failed++
    }
  }
  
  return { success, failed, skipped }
}
